import React, { useState } from 'react';
import { SearchIcon, DownloadIcon, ChevronDownIcon, EyeIcon, ArrowLeftIcon } from 'lucide-react';
export function BulkReceiving() {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [status, setStatus] = useState<'all' | 'pending' | 'in-progress' | 'completed'>('all');
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  // Bank information - hardcoded for this bank portal
  const bankInfo = {
    code: 'AMHRETAA',
    name: 'Amhara Bank S.C.'
  };
  // Generate random bank BICs
  const bankBICs = ['AMHRETAA', 'CBETETAA', 'DASHETAA', 'BUNAETAA', 'NIBIETAA', 'WEGAETAA', 'AWINETAA', 'ZEMEAA', 'COOPETAA', 'ABYSETAA', 'BERHETAA', 'ADDETAA', 'EBCETAA', 'LIOETAA', 'UNITETAA' // United Bank
  ];
  // Corporate client names for receiving payments
  const corporateClients = ['Ethiopian Electric Utility', 'Addis Ababa Water & Sewerage Authority', 'Ethio Telecom', 'Ethiopian Airlines', 'Ethiopian Insurance Corporation', 'Addis Ababa City Administration', 'Ethiopian Revenue & Customs Authority', 'Ethiopian Road Authority', 'Ethiopian Broadcasting Corporation', 'Ethiopian Universities', 'Ethiopian Postal Service', 'Ethiopian Airlines Skylight Hotel', 'Ethiopian Petroleum Supply Enterprise', 'Ethiopian Trading Enterprise', 'Ethiopian Railways Corporation', 'Ethiopian Shipping Lines', 'Ethiopian Sugar Corporation', 'Ethiopian Agricultural Transformation Agency', 'Ethiopian Investment Commission', 'Ethiopian Tourism Organization'];
  // Common Ethiopian first names
  const firstNames = ['Abebe', 'Kebede', 'Tadesse', 'Girma', 'Bekele', 'Alemayehu', 'Tesfaye', 'Haile', 'Solomon', 'Dawit', 'Yohannes', 'Mulugeta', 'Getachew', 'Dereje', 'Mekonnen', 'Tigist', 'Hiwot', 'Selamawit', 'Meseret', 'Yordanos', 'Meron', 'Rahel', 'Bethlehem', 'Kidist', 'Tsion'];
  // Common Ethiopian last names
  const lastNames = ['Abebe', 'Kebede', 'Tadesse', 'Girma', 'Bekele', 'Tesfaye', 'Haile', 'Solomon', 'Dawit', 'Yohannes', 'Mulugeta', 'Getachew', 'Dereje', 'Mekonnen', 'Wolde', 'Gebre', 'Tekle', 'Negash', 'Assefa', 'Ayele'];
  // Generate random batch payment history
  const generateRandomBatchHistory = () => {
    const types = ['Utility Collections', 'Loan Repayments', 'Insurance Premiums', 'Tax Collections', 'School Fees'];
    const statuses = ['Pending', 'In Progress', 'Completed'];
    const fileNames = ['Utility_Collections_May2023.csv', 'Loan_Repayments_Q2_2023.csv', 'Insurance_Premiums_June2023.xlsx', 'Tax_Collections_Q2_2023.csv', 'School_Fees_May2023.xlsx', 'Water_Bill_Collections_June2023.csv', 'Electricity_Bill_Payments_May2023.xlsx', 'Telecom_Bill_Collections_June2023.csv', 'Rent_Collections_May2023.xlsx', 'Membership_Fees_Q2_2023.csv', 'Subscription_Payments_June2023.xlsx', 'Invoice_Collections_Q2_2023.csv', 'Mortgage_Payments_May2023.xlsx', 'Credit_Card_Repayments_June2023.csv', 'Pension_Contributions_July2023.xlsx', 'University_Fees_June2023.csv', 'Medical_Insurance_Premiums_Q2_2023.xlsx', 'Property_Tax_Collections_May2023.csv'];
    // Current date for realistic date generation
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    return Array.from({
      length: 30
    }, (_, i) => {
      // Generate a random date within the last 3 months
      const randomDaysAgo = Math.floor(Math.random() * 90);
      const batchDate = new Date(currentDate);
      batchDate.setDate(batchDate.getDate() - randomDaysAgo);
      const formattedDate = batchDate.toISOString().split('T')[0];
      const hours = String(Math.floor(Math.random() * 12) + 8).padStart(2, '0'); // Business hours 8AM-8PM
      const minutes = String(Math.floor(Math.random() * 60)).padStart(2, '0');
      const seconds = String(Math.floor(Math.random() * 60)).padStart(2, '0');
      const formattedTime = `${hours}:${minutes}:${seconds}`;
      const dateTime = `${formattedDate} ${formattedTime}`;
      const totalRecords = Math.floor(Math.random() * 900) + 100; // 100-1000 records
      // Status should be more likely to be completed for older batches
      let status;
      if (randomDaysAgo > 60) {
        status = Math.random() < 0.9 ? 'Completed' : 'In Progress';
      } else if (randomDaysAgo > 30) {
        status = Math.random() < 0.7 ? 'Completed' : Math.random() < 0.5 ? 'In Progress' : 'Pending';
      } else {
        status = statuses[Math.floor(Math.random() * statuses.length)];
      }
      const successRate = status === 'Completed' ? Math.floor(Math.random() * 11) + 90 :
      // 90-100% success for completed
      status === 'In Progress' ? Math.floor(Math.random() * 30) + 60 :
      // 60-90% for in progress
      0; // 0% for pending
      const successRecords = Math.round(totalRecords * successRate / 100);
      const failedRecords = totalRecords - successRecords;
      const type = types[Math.floor(Math.random() * types.length)];
      // For most batches, sender is another bank, but sometimes it's internal
      const senderBank = Math.random() < 0.8 ? bankBICs.filter(bic => bic !== bankInfo.code)[Math.floor(Math.random() * (bankBICs.length - 1))] : bankInfo.code;
      // Generate batch ID with year, month, day and sequence
      const batchId = `BR-${batchDate.getFullYear()}${String(batchDate.getMonth() + 1).padStart(2, '0')}${String(batchDate.getDate()).padStart(2, '0')}-${String(i + 1).padStart(3, '0')}`;
      return {
        id: batchId,
        type,
        dateTime,
        fileName: fileNames[Math.floor(Math.random() * fileNames.length)],
        totalRecords,
        status,
        successRecords,
        failedRecords,
        successRate,
        senderBank,
        receiverBank: bankInfo.code,
        batchDate: batchDate // Store the actual date object for easier filtering
      };
    });
  };
  // Generate random transactions for a batch
  const generateRandomTransactions = (batchId: string) => {
    // Find the batch to get its date
    const batch = batchHistory.find(b => b.id === batchId);
    const batchDate = batch ? batch.batchDate : new Date();
    // Payment purposes based on the batch type
    const batchType = batch ? batch.type : 'Utility Collections';
    let purposes: string[] = [];
    let toAccountPrefix = '1000';
    let toAccountNameGenerator = () => corporateClients[Math.floor(Math.random() * corporateClients.length)];
    // Set appropriate purposes and account prefixes based on batch type
    if (batchType.includes('Utility')) {
      purposes = ['Water Bill', 'Electricity Bill', 'Telecom Bill', 'Internet Bill', 'Cable TV Bill'];
      toAccountPrefix = '2000'; // Utility accounts
    } else if (batchType.includes('Loan')) {
      purposes = ['Loan Repayment', 'Mortgage Payment', 'Credit Card Payment', 'Personal Loan Payment', 'Business Loan Payment'];
      toAccountPrefix = '3000'; // Loan accounts
    } else if (batchType.includes('Insurance')) {
      purposes = ['Health Insurance', 'Life Insurance', 'Auto Insurance', 'Property Insurance', 'Travel Insurance'];
      toAccountPrefix = '4000'; // Insurance accounts
    } else if (batchType.includes('Tax')) {
      purposes = ['Income Tax', 'Property Tax', 'Value Added Tax', 'Capital Gains Tax', 'Corporate Tax'];
      toAccountPrefix = '5000'; // Tax accounts
    } else if (batchType.includes('School')) {
      purposes = ['Tuition Fee', 'Registration Fee', 'Examination Fee', 'Library Fee', 'Laboratory Fee'];
      toAccountPrefix = '6000'; // Education accounts
    }
    // Number of transactions - either the batch total or a sample of up to 50
    const transactionCount = batch ? batch.totalRecords > 50 ? 50 : batch.totalRecords : Math.floor(Math.random() * 30) + 20;
    return Array.from({
      length: transactionCount
    }, (_, i) => {
      const amount = Math.floor(Math.random() * 4900) + 100; // 100 to 5,000 ETB
      const purpose = purposes[Math.floor(Math.random() * purposes.length)];
      // Generate realistic account numbers
      const fromAccount = `${Math.random() < 0.5 ? '7000' : '8000'}${Math.floor(Math.random() * 10000000)}`;
      const toAccount = `${toAccountPrefix}${Math.floor(Math.random() * 10000000)}`;
      // Generate realistic account names
      const fromAccountName = `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
      const toAccountName = toAccountNameGenerator();
      // Transaction date should be same as batch date or slightly before
      const txnDate = new Date(batchDate);
      txnDate.setHours(0, 0, 0, 0);
      if (Math.random() < 0.3) {
        txnDate.setDate(txnDate.getDate() - Math.floor(Math.random() * 3)); // Some transactions from 1-3 days before
      }
      // Generate transaction ID with batch ID and sequence
      const txnId = `${batchId}-TXN${String(i + 1).padStart(4, '0')}`;
      // For utility payments, add reference numbers
      let referenceNumber = '';
      if (batchType.includes('Utility')) {
        referenceNumber = `${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
      } else if (batchType.includes('Tax')) {
        referenceNumber = `TIN${Math.floor(Math.random() * 10000000).toString().padStart(7, '0')}`;
      }
      const narrationSuffix = referenceNumber ? ` - Ref: ${referenceNumber}` : '';
      return {
        batchId,
        fromAccount,
        fromAccountName,
        toAccount,
        toAccountName,
        amount,
        purpose,
        narration: `${purpose} Payment - ${fromAccountName} - ${txnDate.toISOString().split('T')[0]}${narrationSuffix}`,
        txnId,
        txnDate: txnDate.toISOString().split('T')[0],
        referenceNumber
      };
    });
  };
  const batchHistory = generateRandomBatchHistory();
  // Filter batch history based on search term, date range, and status
  const filteredBatchHistory = batchHistory.filter(batch => {
    // Filter by search term
    if (searchTerm && !batch.id.toLowerCase().includes(searchTerm.toLowerCase()) && !batch.fileName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Filter by status
    if (status !== 'all') {
      if (status === 'pending' && batch.status !== 'Pending') return false;
      if (status === 'in-progress' && batch.status !== 'In Progress') return false;
      if (status === 'completed' && batch.status !== 'Completed') return false;
    }
    // Filter by date (simplified for demo)
    // In a real app, you would parse the dates and compare them properly
    if (dateRange === 'today' && !batch.dateTime.includes(new Date().toISOString().split('T')[0])) return false;
    if (dateRange === 'week') {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      if (batch.batchDate < oneWeekAgo) return false;
    }
    if (dateRange === 'month') {
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      if (batch.batchDate < oneMonthAgo) return false;
    }
    if (dateRange === 'custom') {
      if (fromDate && toDate) {
        const batchDateStr = batch.dateTime.split(' ')[0];
        if (batchDateStr < fromDate || batchDateStr > toDate) return false;
      }
    }
    return true;
  });
  const handleViewDetails = (batchId: string) => {
    setSelectedBatch(batchId);
    setShowTransactionDetails(true);
  };
  const handleDownloadReport = (batchId: string) => {
    // In a real app, this would download a report for the batch
    alert(`Downloading XLSX report for batch ${batchId}`);
  };
  const handleBackToList = () => {
    setShowTransactionDetails(false);
    setSelectedBatch(null);
  };
  return <div className="space-y-6" data-id="element-423">
      {!showTransactionDetails ? <>
          <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-424">Bulk Receiving</h1>
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-425">
            <div className="p-6" data-id="element-426">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-427">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-428">
                  Payment History
                </h2>
                <div className="flex flex-wrap gap-3" data-id="element-429">
                  <div className="relative" data-id="element-430">
                    <button onClick={() => setShowDateFilter(!showDateFilter)} className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-431">
                      {dateRange === 'today' && 'Today'}
                      {dateRange === 'week' && 'This Week'}
                      {dateRange === 'month' && 'This Month'}
                      {dateRange === 'custom' && 'Custom Range'}
                      <ChevronDownIcon size={16} className="ml-2" data-id="element-432" />
                    </button>
                    {showDateFilter && <div className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-433">
                        <div className="py-1" data-id="element-434">
                          <button onClick={() => {
                      setDateRange('today');
                      setShowDateFilter(false);
                    }} className={`block w-full text-left px-4 py-2 text-sm ${dateRange === 'today' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-435">
                            Today
                          </button>
                          <button onClick={() => {
                      setDateRange('week');
                      setShowDateFilter(false);
                    }} className={`block w-full text-left px-4 py-2 text-sm ${dateRange === 'week' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-436">
                            This Week
                          </button>
                          <button onClick={() => {
                      setDateRange('month');
                      setShowDateFilter(false);
                    }} className={`block w-full text-left px-4 py-2 text-sm ${dateRange === 'month' ? 'bg-gray-100 text-[#F58220]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-437">
                            This Month
                          </button>
                          <div className="border-t border-gray-100 my-1" data-id="element-438"></div>
                          <div className="px-4 py-2" data-id="element-439">
                            <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-440">
                              Custom Range
                            </p>
                            <div className="space-y-2" data-id="element-441">
                              <div data-id="element-442">
                                <label className="text-xs text-gray-500" data-id="element-443">
                                  From
                                </label>
                                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full mt-1 text-sm border border-gray-300 rounded-md px-2 py-1" data-id="element-444" />
                              </div>
                              <div data-id="element-445">
                                <label className="text-xs text-gray-500" data-id="element-446">
                                  To
                                </label>
                                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full mt-1 text-sm border border-gray-300 rounded-md px-2 py-1" data-id="element-447" />
                              </div>
                              <button onClick={() => {
                          if (fromDate && toDate) {
                            setDateRange('custom');
                            setShowDateFilter(false);
                          }
                        }} disabled={!fromDate || !toDate} className={`w-full mt-2 px-3 py-1 text-xs rounded-md ${fromDate && toDate ? 'bg-[#F58220] text-white' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`} data-id="element-448">
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-449">
                    <button onClick={() => setStatus('all')} className={`px-3 py-2 text-xs rounded-md ${status === 'all' ? 'bg-gray-200 text-gray-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-450">
                      All Status
                    </button>
                    <button onClick={() => setStatus('pending')} className={`px-3 py-2 text-xs rounded-md ${status === 'pending' ? 'bg-blue-100 text-blue-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-451">
                      Pending
                    </button>
                    <button onClick={() => setStatus('in-progress')} className={`px-3 py-2 text-xs rounded-md ${status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-452">
                      In Progress
                    </button>
                    <button onClick={() => setStatus('completed')} className={`px-3 py-2 text-xs rounded-md ${status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-300 text-gray-700'}`} data-id="element-453">
                      Completed
                    </button>
                  </div>
                  <div className="relative" data-id="element-454">
                    <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" data-id="element-455" />
                    <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" data-id="element-456" />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-457">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-458">
                  <thead className="bg-gray-50" data-id="element-459">
                    <tr data-id="element-460">
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-461">
                        Batch ID
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-462">
                        Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-463">
                        Date & Time
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-464">
                        File Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-465">
                        Records
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-466">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-467">
                        Success
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-468">
                        Failed
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-469">
                        Success Rate
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-470">
                        Sender Bank
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-471">
                        Receiver Bank
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-472">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-473">
                    {filteredBatchHistory.length > 0 ? filteredBatchHistory.map(batch => <tr key={batch.id} className="hover:bg-gray-50" data-id="element-474">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-[#F58220]" data-id="element-475">
                            {batch.id}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-476">
                            {batch.type}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-477">
                            {batch.dateTime}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-478">
                            {batch.fileName}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-479">
                            {batch.totalRecords}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap" data-id="element-480">
                            <span className={`px-2 py-1 text-xs rounded-full ${batch.status === 'Pending' ? 'bg-blue-100 text-blue-800' : batch.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-481">
                              {batch.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-482">
                            {batch.successRecords}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-483">
                            {batch.failedRecords}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-484">
                            {batch.status === 'Pending' ? <span className="text-blue-600" data-id="element-485">Pending</span> : <span data-id="element-486">{batch.successRate}%</span>}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-487">
                            {batch.senderBank}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-488">
                            {batch.receiverBank}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-489">
                            <div className="flex items-center space-x-3" data-id="element-490">
                              <button onClick={() => handleViewDetails(batch.id)} className="text-indigo-600 hover:text-indigo-900 flex items-center" title="View Details" data-id="element-491">
                                <EyeIcon size={16} data-id="element-492" />
                              </button>
                              {batch.status === 'Completed' && <button onClick={() => handleDownloadReport(batch.id)} className="text-gray-600 hover:text-gray-900 flex items-center" title="Download Report" data-id="element-493">
                                  <DownloadIcon size={16} data-id="element-494" />
                                </button>}
                            </div>
                          </td>
                        </tr>) : <tr data-id="element-495">
                        <td colSpan={12} className="px-4 py-4 text-center text-sm text-gray-500" data-id="element-496">
                          No batch payments found matching your criteria
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </> : <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden" data-id="element-497">
          <div className="p-6" data-id="element-498">
            <div className="flex items-center mb-6" data-id="element-499">
              <button className="mr-4 p-2 rounded-full hover:bg-gray-100" onClick={handleBackToList} data-id="element-500">
                <ArrowLeftIcon size={20} data-id="element-501" />
              </button>
              <h1 className="text-xl font-semibold text-[#1A1F36]" data-id="element-502">
                Transaction Details: {selectedBatch}
              </h1>
            </div>
            <div className="mb-6 flex justify-between items-center" data-id="element-503">
              <div className="text-sm text-gray-600" data-id="element-504">
                Showing all transactions for this batch
              </div>
              <button onClick={() => handleDownloadReport(selectedBatch!)} className="flex items-center bg-[#F58220] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#e07619]" data-id="element-505">
                <DownloadIcon size={16} className="mr-2" data-id="element-506" />
                Download Report
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-507">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-508">
                <thead className="bg-gray-50" data-id="element-509">
                  <tr data-id="element-510">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-511">
                      Transaction ID
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-512">
                      Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-513">
                      From Account
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-514">
                      From Account Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-515">
                      To Account
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-516">
                      To Account Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-517">
                      Amount
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-518">
                      Purpose
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-519">
                      Reference
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-520">
                      Narration
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-521">
                  {generateRandomTransactions(selectedBatch!).map((txn, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-522">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-[#F58220]" data-id="element-523">
                          {txn.txnId}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-524">
                          {txn.txnDate}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-525">
                          {txn.fromAccount}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-526">
                          {txn.fromAccountName}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-527">
                          {txn.toAccount}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-528">
                          {txn.toAccountName}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-529">
                          ETB {txn.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-530">
                          {txn.purpose}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-531">
                          {txn.referenceNumber || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate" data-id="element-532">
                          {txn.narration}
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}